import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IRankingApiProvider, IUserRank} from "data/providers/api/ranking.api.provider";
import {ModalType} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";

export interface IRankingStore {
	get userRank(): IUserRank | null;
	get maxUserRank(): number | null;

	getUserRank(): Promise<void>;
}

@injectable()
export class RankingStore implements IRankingStore {
	@observable private _userRank: IUserRank | null = null;
	@observable private _maxRank: number | null = null;

	get userRank() {
		return this._userRank;
	}

	get maxUserRank() {
		return this._maxRank;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.RankingApiProvider) private _rankingApi: IRankingApiProvider
	) {
		makeAutoObservable(this);
	}

	private errorMessage = () => {
		return this._modalStore.showModal(ModalType.ERROR, {
			title: this.i18n.t("modals.error.title", "Error"),
			message: this.i18n.t(
				"modals.error.description",
				"Ups, something went wrong, please try again later."
			),
		});
	};

	@action
	async getUserRank() {
		try {
			const response = await this._rankingApi.dashboard();
			const {userRanking, maxRank} = response.data.success;

			runInAction(() => {
				this._userRank = userRanking;
				this._maxRank = maxRank;
			});
		} catch (err) {
			this.errorMessage();
		}
	}
}
